import React, { createRef, FC, RefObject, useEffect, useRef, useState } from 'react';

import { compareDates } from '@utils/helpers';
import { Discount, Price } from 'shared-types/Common';
import { Moment } from 'moment';
import { Slider, Wrapper } from './DateSlider.styles';
import { SliderDateField } from './SliderDateField';
import { some } from '@utils/some';

type DateSliderProps = {
  dates: {
    date: Moment;
    price?: Price;
    disableDate?: boolean;
    discount?: Discount;
  }[];
  price?: Price;
  input: {
    value: Moment;
    onChange: (date: Moment) => void;
  };
  handleAfterChange?: (val?: Moment) => void;
  hasPrices?: boolean;
  visitDate?: Moment;
  loadingDates?: string[];
  telephoneOnly: boolean;
  isLoading: boolean;
  checkAllDays?: boolean;
  displayOnly?: boolean;
};

const useSelectedDate = ({
  dates,
  loadingDates,
  input,
}: Pick<DateSliderProps, 'dates' | 'loadingDates' | 'input'>) => {
  const hasStartedLoadingDates = useRef(false);
  const [isLoading, setIsLoading] = useState(some(loadingDates));

  if (loadingDates && loadingDates.length > 0) {
    hasStartedLoadingDates.current = true;
  }

  useEffect(() => {
    if (loadingDates?.length === 0 && hasStartedLoadingDates.current) {
      setIsLoading(false);
    }
  }, [loadingDates]);

  let selectedDate: undefined | Moment = undefined;

  if (!isLoading) {
    selectedDate =
      (dates.find((date) => compareDates(date.date, input.value) && !date.disableDate)
        ?.date as Moment) || (dates.find((date) => !date.disableDate)?.date as Moment);
  }

  return selectedDate;
};

export const DatesSlider: FC<DateSliderProps> = ({
  dates = [],
  input,
  handleAfterChange = () => {},
  hasPrices = false,
  visitDate,
  loadingDates,
  telephoneOnly,
  isLoading,
  checkAllDays = false,
  displayOnly,
}) => {
  const selectedDate = useSelectedDate({ dates, loadingDates, input });

  const refsMap = new Map<string, RefObject<HTMLInputElement>>();
  const selectedDay = selectedDate?.format('YYYY-MM-DD');
  const visitDay = visitDate?.format('YYYY-MM-DD');

  useEffect(() => {
    if (selectedDate && selectedDay && selectedDay !== visitDay) {
      input.onChange(selectedDate);
      handleAfterChange(selectedDate);
    }
  }, [selectedDate, selectedDay, visitDay]);
  return (
    <Wrapper displayOnly={displayOnly}>
      <Slider>
        {dates &&
          dates.map((value, index) => {
            const radioRef = createRef<HTMLInputElement>();
            const date = value.date.format('YYYY-MM-DD');
            refsMap.set(date, radioRef);
            return (
              <SliderDateField
                key={value.date.format()}
                value={{ price: value.price as Price, date, discount: value.discount }}
                isChecked={
                  checkAllDays || (value ? value.date.format('YYYY-MM-DD') === visitDay : false)
                }
                onChange={(val: Moment) => {
                  input.onChange(val);
                  handleAfterChange(val);
                  if (selectedDate?.format('YYYY-MM-DD')) {
                    refsMap
                      .get(selectedDate.format('YYYY-MM-DD'))
                      ?.current?.removeAttribute('checked');
                  }
                }}
                isDisabled={isLoading || (!telephoneOnly && value.disableDate)}
                isLoading={loadingDates?.includes(date) ?? false}
                isPriceVisible={hasPrices}
                radioRef={radioRef}
                id={`date-slider-${index}`}
              />
            );
          })}
      </Slider>
    </Wrapper>
  );
};
